import React, { ChangeEvent, useEffect, useMemo, useRef } from "react";
import { Row, Col, Input, Checkbox, Space, Button, Popover } from "antd";
import { BsSliders } from "react-icons/bs";
import { Company } from "src/entity/map-company-user/map-company-user";
import { debounce } from "lodash";
import { componentPalette } from "src/theme/component-palette";
import { AreaProps, CompanyGroupProps } from "src/entity/company/company-response.entity";
import { EnumFilterMapCompany, LabelValueType } from "src/entity/company/company.constants";
import { FilterSearchCompanyMap } from "src/entity/company/company-mapping.entity";

interface SearchFilterProps {
  placeholder: string;
  action: (filter: FilterSearchCompanyMap) => Promise<void>;
  filterRegion: string[];
  setFilterRegion: (value: string[]) => void;
  filterOrganization: string[];
  setFilterOrganization: (value: string[]) => void;
  filterCompanyGroup: string[];
  setFilterCompanyGroup: (value: string[]) => void;
  filterCompany: string[];
  setFilterCompany: (value: string[]) => void;
  textSearch: string;
  setTextSearch: (value: string) => void;

  setPage: (page: number) => void;

  dataRegion: LabelValueType[];
  dataOrganization: LabelValueType[];
  dataCompanyGroup: CompanyGroupProps[];
  dataArea: AreaProps[];
  dataCompanies: Company[];
}

const chunkSize = 6;

export const SearchCompany: React.FunctionComponent<SearchFilterProps> = ({ ...props }) => {
  const {
    action,
    filterRegion,
    setFilterRegion,
    filterOrganization,
    setFilterOrganization,
    filterCompanyGroup,
    setFilterCompanyGroup,
    filterCompany,
    setFilterCompany,
    textSearch,
    setTextSearch,
    dataRegion,
    dataOrganization,
    dataCompanyGroup,
    dataArea,
    dataCompanies,
    setPage,
  } = props;

  const getChunkCompanyFilter = useMemo(() => {
    if (!Array.isArray(dataCompanies)) return [];

    const chunks = [];
    for (let i = 0; i < dataCompanies.length; i += chunkSize) {
      chunks.push(dataCompanies.slice(i, i + chunkSize));
    }
    return chunks;
  }, [dataCompanies]);

  const debounceRequestApi = useRef(
    debounce((textSearch, filterRegion, filterOrganization, filterCompanyGroup, filterCompany) => {
      action({
        textSearch,
        regions: filterRegion as string[],
        organizations: filterOrganization as string[],
        companyGroupId: filterCompanyGroup as string[],
        company: filterCompany as string[],
      });
    }, 500),
  ).current;

  useEffect(() => {
    debounceRequestApi(textSearch, filterRegion, filterOrganization, filterCompanyGroup, filterCompany);
  }, [textSearch, filterRegion, filterOrganization, filterCompanyGroup, filterCompany, debounceRequestApi]);

  const resetFilter = () => {
    setFilterRegion([]);
    setFilterOrganization([]);
    setFilterCompanyGroup([]);
    setFilterCompany([]);
  };

  const onChangeFilter = (checkboxValue: string[], type: string) => {
    switch (type) {
      case EnumFilterMapCompany.REGION:
        setFilterRegion(checkboxValue);
        break;
      case EnumFilterMapCompany.ORGANIZATION:
        setFilterOrganization(checkboxValue);
        break;
      case EnumFilterMapCompany.COMPANY_GROUP:
        setFilterCompanyGroup(checkboxValue);
        break;
      case EnumFilterMapCompany.COMPANY:
        setFilterCompany(checkboxValue);
        break;
      default:
        break;
    }
    setPage(1);
  };

  const content = (
    <Col className="" style={{ maxWidth: "80vw" }}>
      <Row gutter={16}>
        <Col>
          <Space direction="vertical">
            <strong>Region</strong>
            <Checkbox.Group style={{ width: "100%" }} value={filterRegion} onChange={(e) => onChangeFilter(e, EnumFilterMapCompany.REGION)}>
              <Space direction="vertical">
                {dataRegion?.map((item, index) => {
                  return (
                    <Checkbox key={"region-checkbox-" + index} value={item.label}>
                      {item.label}
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </Space>
        </Col>
        <Col>
          <Space direction="vertical">
            <strong>Organization Type</strong>
            <Checkbox.Group
              style={{ width: "100%" }}
              value={filterOrganization}
              onChange={(e) => onChangeFilter(e, EnumFilterMapCompany.ORGANIZATION)}
            >
              <Space direction="vertical">
                {dataOrganization?.map((item, index) => {
                  return (
                    <Checkbox key={"org-checkbox-" + index} value={item.value}>
                      {item.label}
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </Space>
        </Col>
        <Col>
          <Space direction="vertical">
            <strong>Company Group</strong>
            <Checkbox.Group
              style={{ width: "100%" }}
              value={filterCompanyGroup}
              onChange={(e) => onChangeFilter(e, EnumFilterMapCompany.COMPANY_GROUP)}
            >
              <Space direction="vertical">
                {dataCompanyGroup?.map((item, index) => {
                  return (
                    <Checkbox key={"company-group-checkbox-" + index} value={item._id}>
                      {item.companyGroupName}
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </Space>
        </Col>
        <Col className="pl-2" style={{ maxWidth: "70vw" }}>
          <Space direction="vertical" style={{ maxWidth: `${getChunkCompanyFilter?.length ? getChunkCompanyFilter.length * 10 : 10}rem` }}>
            <strong>Company</strong>
            <Checkbox.Group
              value={filterCompany}
              onChange={(e) => onChangeFilter(e, EnumFilterMapCompany.COMPANY)}
              style={{ maxWidth: `${getChunkCompanyFilter?.length ? getChunkCompanyFilter.length * 10 : 10}rem` }}
            >
              <Row className="w-full" gutter={[16, 16]}>
                {getChunkCompanyFilter.map((chunk, index) => (
                  <Space direction="vertical" key={index} style={{ minWidth: "5rem", maxWidth: "10rem" }}>
                    {chunk.map((item, idx) => (
                      <Checkbox key={"checkbox-" + idx} value={item._id}>
                        {item.companyName}
                      </Checkbox>
                    ))}
                  </Space>
                ))}
              </Row>
            </Checkbox.Group>
          </Space>
        </Col>
      </Row>
      <Row justify={"space-between"} className="pt-2">
        <Button size="small" onClick={resetFilter}>
          Reset
        </Button>
        <div />
      </Row>
    </Col>
  );

  const isSomeFilter = useMemo(() => {
    return filterCompany.length || filterRegion.length || filterCompanyGroup.length || filterCompany.length;
  }, [filterCompany, filterRegion, filterCompanyGroup, filterCompany]);

  const suffix = (
    <Popover content={content} title="" trigger={["hover", "click"]} placement="bottom">
      {/* <Button ghost size="small"> */}
      <BsSliders style={{ cursor: "pointer" }} color={isSomeFilter ? componentPalette.primary : componentPalette.black} />
      {/* </Button> */}
    </Popover>
  );

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setTextSearch(e.target.value);
  };

  return (
    <>
      <Input placeholder="input search text" onChange={onSearch} className="w-1/5" suffix={suffix} />
    </>
  );
};
